import { useEffect, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import {
  Feature,
  ListingClient,
  ListingExclusivityClass,
} from 'src/WebApiController';

/**
 * POS-9467: classifies a batch of merge groups (each a set of listing ids) and reports, per group and
 * in the same order, which exclusivity classes it mixes. Empty for a group that is homogeneous.
 *
 * A merge suggestion is exactly the set the merge will run on, so this needs no server-side help.
 */
export const useMergeExclusivityWarnings = (listingIdSets: number[][]) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const hasMergeExclusivityWarningFeature = useUserHasFeature(
    Feature.ListingMergeExclusivityWarning
  );

  // Keyed by value so the effect only refires when the groups actually change, not on every render.
  const setsKey = JSON.stringify(listingIdSets);
  const [sourceClassesByGroup, setSourceClassesByGroup] = useState<
    ListingExclusivityClass[][]
  >([]);

  useEffect(() => {
    const sets = JSON.parse(setsKey) as number[][];
    const noneMixed: ListingExclusivityClass[][] = sets.map(() => []);

    if (
      !hasMergeExclusivityWarningFeature ||
      sets.length === 0 ||
      sets.some((s) => s.length < 2)
    ) {
      setSourceClassesByGroup(noneMixed);
      return;
    }

    let cancelled = false;
    new ListingClient(activeAccountWebClientConfig)
      .getMergeExclusivityWarnings(sets)
      .then((results) => {
        if (!cancelled) {
          setSourceClassesByGroup(
            (results ?? []).map((r) =>
              r?.hasMixedSourceClasses ? r.sourceClasses : []
            )
          );
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSourceClassesByGroup(noneMixed);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [
    activeAccountWebClientConfig,
    hasMergeExclusivityWarningFeature,
    setsKey,
  ]);

  return sourceClassesByGroup;
};
